<template>
  <div class="expert-profile-view">
    <div class="d-flex align-center mb-6">
      <v-btn icon class="mr-2 no-shadow" @click="goBack">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <h1 class="text-h4 font-weight-bold">Experts Profile</h1>
      <v-spacer></v-spacer>
      <div class="expert-qualification">
        <div class="text-subtitle-2 mb-1">Expert Qualification</div>
        <v-rating
          v-model="expert.rating"
          color="#e94057"
          background-color="grey-lighten-2"
          empty-icon="mdi-star-outline"
          full-icon="mdi-star"
          length="5"
          hover
          class="qualification-rating"
          size="20"
        ></v-rating>
      </div>
    </div>
    
    <!-- Expert Profile Card -->
    <v-card class="expert-profile-card mb-4 elevation-0">
      <v-card-text>
        <div class="d-flex flex-wrap">
          <!-- Left column - Photo -->
          <div class="profile-left-column mr-6">
            <v-avatar size="150" class="expert-avatar">
              <v-img :src="expert.photoUrl || 'https://via.placeholder.com/150'" alt="Expert Photo"></v-img>
            </v-avatar>
          </div>
          
          <!-- Right column - Form fields -->
          <div class="profile-right-column">
            <!-- General error messages -->
            <div v-if="formErrors.non_field_errors || formErrors.detail" class="error-message-section mb-4">
              <div class="error-message-box">
                <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
                <span>{{ formErrors.non_field_errors || formErrors.detail }}</span>
              </div>
            </div>
            
            <!-- Basic Information -->
            <div class="mb-4">
              <v-row dense>
                <v-col cols="12" md="6">
                  <div class="text-subtitle-2 mb-1">First Name*</div>
                  <BaseInput
                    id="first-name"
                    v-model="expert.firstName"
                    required
                    dense
                    :error="formErrors.first_name"
                  ></BaseInput>
                  <div v-if="formErrors.first_name" class="error-message">{{ formErrors.first_name }}</div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="text-subtitle-2 mb-1">Last Name*</div>
                  <BaseInput
                    id="last-name"
                    v-model="expert.lastName"
                    required
                    dense
                    :error="formErrors.last_name"
                  ></BaseInput>
                  <div v-if="formErrors.last_name" class="error-message">{{ formErrors.last_name }}</div>
                </v-col>
              </v-row>
              
              <v-row dense>
                <v-col cols="12" md="6">
                  <div class="text-subtitle-2 mb-1">Email Address*</div>
                  <BaseInput
                    id="email"
                    v-model="expert.email"
                    dense
                    :error="formErrors.email"
                  ></BaseInput>
                  <div v-if="formErrors.email" class="error-message">{{ formErrors.email }}</div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="text-subtitle-2 mb-1">Mobile Phone*</div>
                  <BaseInput
                    id="mobile-phone"
                    v-model="expert.mobilePhone"
                    required
                    dense
                    :error="formErrors.mobile_phone"
                  ></BaseInput>
                  <div v-if="formErrors.mobile_phone" class="error-message">{{ formErrors.mobile_phone }}</div>
                </v-col>
              </v-row>
              
              <v-row dense>
                <v-col cols="12" md="6">
                  <div class="text-subtitle-2 mb-1">LinkedIn Profile URL</div>
                  <BaseInput
                    id="linkedin-profile-url"
                    v-model="expert.linkedinProfileUrl"
                    placeholder="https://linkedin.com/in/your-profile"
                    dense
                    :error="formErrors.linkedin_profile_url"
                  ></BaseInput>
                  <div v-if="formErrors.linkedin_profile_url" class="error-message">{{ formErrors.linkedin_profile_url }}</div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="text-subtitle-2 mb-1">Company you work for*</div>
                  <BaseInput
                    id="current-company"
                    v-model="expert.currentCompany"
                    placeholder="Company name"
                    required
                    dense
                    :error="formErrors.current_company"
                  ></BaseInput>
                  <div v-if="formErrors.current_company" class="error-message">{{ formErrors.current_company }}</div>
                </v-col>
              </v-row>
              
              <v-row dense>
                <v-col cols="12" md="4">
                  <div class="text-subtitle-2 mb-1">Gender*</div>
                  <RadioGroup
                    v-model="expert.gender"
                    name="gender"
                    :options="[
                      { value: 'female', text: 'Female' },
                      { value: 'male', text: 'Male' }
                    ]"
                    compact
                    :error="formErrors.gender"
                  />
                  <div v-if="formErrors.gender" class="error-message">{{ formErrors.gender }}</div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="text-subtitle-2 mb-1">Age*</div>
                  <div class="d-flex align-center">
                    <v-chip outlined small class="age-chip-compact">
                      {{ expert.age }} Years
                    </v-chip>
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="text-subtitle-2 mb-1">Works for Coca-Cola</div>
                  <v-switch
                    v-model="expert.worksForCoke"
                    color="#e94057"
                    density="compact"
                    hide-details
                  ></v-switch>
                </v-col>
              </v-row>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
    
    <!-- Assessment Participation Table -->
    <v-card class="participation-card mb-4 elevation-0">
      <v-card-title>
        <h2 class="text-h6 font-weight-bold">Assessment Participation History</h2>
      </v-card-title>
      <v-card-text>
        <v-table class="participation-table">
          <thead>
            <tr>
              <th>Assessment Title</th>
              <th>Assigned Date</th>
              <th>Deadline</th>
              <th>Response Status</th>
              <th>Completion</th>
              <th>Payment Status</th>
            </tr>
          </thead>
          <tbody>
            <!-- Loading State -->
            <tr v-if="loadingParticipation">
              <td colspan="6" class="text-center py-8">
                <v-progress-circular indeterminate color="#e94057"></v-progress-circular>
                <p class="mt-4 text-grey">Loading participation data...</p>
              </td>
            </tr>
            
            <!-- Empty State -->
            <tr v-else-if="!participationData || participationData.length === 0">
              <td colspan="6" class="text-center py-8">
                <v-icon size="64" color="grey-lighten-1">mdi-inbox</v-icon>
                <p class="mt-4 text-grey">No assessment participation yet</p>
              </td>
            </tr>
            
            <!-- Data Rows -->
            <tr v-else v-for="item in participationData" :key="item.assessment_id">
              <td>
                <div class="assessment-title">{{ item.assessment_title }}</div>
              </td>
              <td>{{ formatDate(item.assessment_created_at) }}</td>
              <td>{{ formatDate(item.assessment_deadline) || 'No deadline' }}</td>
              <td>
                <v-chip 
                  :color="getResponseStatusColor(item.response_status)" 
                  size="small"
                  class="status-chip"
                >
                  {{ formatResponseStatus(item.response_status) }}
                </v-chip>
              </td>
              <td>
                <div class="completion-info">
                  <span class="completion-text">{{ item.completion_percentage }}%</span>
                  <v-progress-linear
                    :model-value="item.completion_percentage"
                    :color="item.response_status === 'completed' ? 'success' : '#e94057'"
                    height="6"
                    rounded
                    class="mt-1"
                  ></v-progress-linear>
                </div>
              </td>
              <td>
                <v-chip 
                  :color="getPaymentStatusColor(item.payment_status)" 
                  size="small"
                  class="status-chip"
                >
                  {{ formatPaymentStatus(item.payment_status) }}
                </v-chip>
                <div v-if="item.payment_amount" class="payment-amount mt-1">
                  {{ item.payment_currency }} {{ item.payment_amount }}
                </div>
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-card-text>
    </v-card>
    
    <!-- Expert Profile Information Card -->
    <v-card class="expert-profile-card mb-4 elevation-0">
      <v-card-text>
        <v-divider class="my-4"></v-divider>
        
        <!-- Expertise Section -->
        <div class="mb-4">
          <div class="text-subtitle-1 mb-3">Expert Profile Information</div>
          
          <!-- General expertise validation error messages -->
          <div v-if="formErrors.expertiseAreas || formErrors.industries || formErrors.geographicExpertise || formErrors.ageExpertise || formErrors.backgroundExpertise" class="error-message-section mb-4">
            <div class="error-message-box">
              <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
              <span>Please complete all required fields in your expertise profile</span>
            </div>
          </div>
          
          <ExpertiseForm
            ref="expertiseForm"
            :selectedBrands="[]"
            :otherBrandExperience="''"
            :selectedExpertiseAreas="selectedExpertiseAreas"
            :otherExpertise="otherExpertise"
            :selectedIndustries="selectedIndustries"
            :otherIndustries="otherIndustries"
            :selectedGeographicExpertise="selectedGeographicExpertise"
            :expertiseLevel="expert.expertiseLevel"
            :ageExpertise="ageExpertise"
            :backgroundData="backgroundData"
            :additionalInfo="additionalInfo"
            :errors="formErrors"
            @toggle-brands="() => {}"
            @update-other-brand-experience="() => {}"
            @toggle-expertise-areas="updateSelectedExpertiseAreas"
            @update-other-expertise="updateOtherExpertise"
            @toggle-industries="updateSelectedIndustries"
            @update-other-industries="updateOtherIndustries"
            @toggle-geographic-expertise="updateSelectedGeographicExpertise"
            @update-expertise-level="updateExpertiseLevel"
            @rate-age-expertise="rateAgeExpertise"
            @rate-background="rateBackground"
            @update-additional-info="updateAdditionalInfo"
          />
          
          <!-- Additional validation error messages for expertise sections -->
          <!-- <div v-if="formErrors.brands" class="error-message mt-2">{{ formErrors.brands }}</div> -->
          <div v-if="formErrors.expertiseAreas" class="error-message mt-2">{{ formErrors.expertiseAreas }}</div>
          <div v-if="formErrors.industries" class="error-message mt-2">{{ formErrors.industries }}</div>
          <div v-if="formErrors.geographicExpertise" class="error-message mt-2">{{ formErrors.geographicExpertise }}</div>
          <div v-if="formErrors.ageExpertise" class="error-message mt-2">{{ formErrors.ageExpertise }}</div>
          <div v-if="formErrors.backgroundExpertise" class="error-message mt-2">{{ formErrors.backgroundExpertise }}</div>
        </div>
      </v-card-text>
      
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <ActionButton
          variant="secondary"
          @click="cancel"
        >
          Cancel
        </ActionButton>
        <ActionButton
          variant="primary"
          @click="saveExpert"
          :loading="loading"
        >
          Save Changes
        </ActionButton>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import apiService from '@/services/apiService';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import RadioGroup from '@/components/common/RadioGroup.vue';
import ExpertiseForm from '@/components/common/ExpertiseForm.vue';
import { useToast } from 'vue-toastification';

export default {
  name: 'ExpertProfileView',
  components: {
    ActionButton,
    BaseInput,
    RadioGroup,
    ExpertiseForm
  },
  
  setup() {
    const toast = useToast();
    return { toast };
  },
  
  data() {
    return {
      loading: false,
      loadingParticipation: false,
      formErrors: {},
      
      // Assessment participation data
      participationData: [],
      
      // Expert data
      expert: {
        id: null,
        firstName: '',
        lastName: '',
        email: '',
        mobilePhone: '',
        photoUrl: '',
        rating: 4,
        expertiseLevel: '',
        gender: '',
        age: 0,
        worksForCoke: false, // Added for the new switch
        linkedinProfileUrl: '',
        currentCompany: ''
      },
      
      // Multi-select arrays and "other" text fields - same structure as AccountView
      // selectedBrands: [],  // REMOVED - No longer collected
      // otherBrandExperience: '',  // REMOVED - No longer collected
      
      // Keep for backward compatibility but not used
      selectedBrands: [],
      otherBrandExperience: '',
      selectedExpertiseAreas: [],
      otherExpertise: '',
      selectedIndustries: [],
      otherIndustries: '',
      selectedGeographicExpertise: [],
      
      // Age expertise ratings
      ageExpertise: {
        children: 0,
        teenagers: 0,
        adults: 0,
        seniors: 0
      },
      
      // Professional background ratings - removed design_experience like AccountView
      backgroundData: {
        agency: 0,
        academia: 0,
        marketing: 0,
        marketResearch: 0
      },
      
      // Additional info
      additionalInfo: ''
    };
  },
  
  mounted() {
    // Scroll to top when component is mounted
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Get expert ID from route params
    const expertId = this.$route.params.id;
    if (expertId) {
      this.fetchExpertDetails(expertId);
      this.fetchParticipationData(expertId);
    }
  },
  
  watch: {
    // Watch for route changes (when navigating between different experts)
    '$route.params.id': {
      handler(newId, oldId) {
        if (newId && newId !== oldId) {
          // Scroll to top when expert changes
          window.scrollTo({ top: 0, behavior: 'smooth' });
          
          // Fetch new expert data
          this.fetchExpertDetails(newId);
          this.fetchParticipationData(newId);
        }
      },
      immediate: false
    }
  },
  
  methods: {
    fetchExpertDetails(expertId) {
      this.loading = true;
      
      apiService.get(`experts/${expertId}/`)
        .then(response => {
          const d = response.data;
          this.expert = {
            id: d.id,
            firstName: d.first_name || d.firstName || '',
            lastName: d.last_name || '',
            email: d.email || (d.user ? d.user.email : ''),
            mobilePhone: d.mobile_phone || '',
            photoUrl: d.profile_picture || d.photoUrl || '',
            rating: d.rating || 0,
            expertiseLevel: d.expertise_level || '',
            gender: d.gender || '',
            age: d.age || 0,
            worksForCoke: d.works_for_coke !== undefined ? d.works_for_coke : (d.worksForCoke || false), // Fetch the new field
            linkedinProfileUrl: d.linkedin_profile_url || '', // Fetch the new field
            currentCompany: d.current_company || d.currentCompany || ''
          };

          // Set multi-select arrays and "other" text fields - using proper ID arrays like AccountView
          // Use brand_ids field if available, otherwise fallback to converting brand names
          // COMMENTED OUT - Brand data no longer needed
          // this.selectedBrands = d.brand_ids || (d.brand_expertise || []).map(brand => {
          //   if (typeof brand === 'object' && brand.id) return brand.id;
          //   if (typeof brand === 'string') return this.convertBrandNameToId(brand);
          //   return brand;
          // }).filter(id => id !== null && id !== undefined);
          // 
          // this.otherBrandExperience = d.other_brand_experience || '';
          
          // Keep for backward compatibility but not used
          this.selectedBrands = [];
          this.otherBrandExperience = '';
          
          this.selectedExpertiseAreas = (d.expertise_areas || []).map(i => (typeof i === 'object' ? i.id : i));
          this.otherExpertise = d.other_expertise || '';
          this.selectedIndustries = (d.industries || []).map(i => (typeof i === 'object' ? i.id : i));
          this.otherIndustries = d.other_industries || '';
          this.selectedGeographicExpertise = (d.geographical_expertise || []).map(i => (typeof i === 'object' ? i.id : i));

          // Age expertise ratings
          this.ageExpertise = {
            children: d.age_expertise_children || 0,
            teenagers: d.age_expertise_teenagers || 0,
            adults: d.age_expertise_adults || 0,
            seniors: d.age_expertise_seniors || 0
          };

          // Professional background ratings - removed design fields like AccountView
          this.backgroundData = {
            agency: d.agency_experience || 0,
            academia: d.academia_experience || 0,
            marketing: d.marketing_experience || 0,
            marketResearch: d.market_research_experience || 0
          };

          // Additional info
          this.additionalInfo = d.additional_info || '';
        })
        .catch(error => {
          console.error('Error fetching expert details:', error);
          this.toast.error('Failed to load expert details');
          this.$router.push('/experts'); // Redirect back to experts list on error
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    fetchParticipationData(expertId) {
      this.loadingParticipation = true;
      
      apiService.get(`experts/${expertId}/participation/`)
        .then(response => {
          this.participationData = response.data.participation || [];
        })
        .catch(error => {
          console.error('Error fetching participation data:', error);
          this.toast.error('Failed to load assessment participation data');
          this.participationData = [];
        })
        .finally(() => {
          this.loadingParticipation = false;
        });
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    },
    
    formatResponseStatus(status) {
      const statusMap = {
        'not_started': 'Not Started',
        'in_progress': 'In Progress',
        'completed': 'Completed'
      };
      return statusMap[status] || status;
    },
    
    getResponseStatusColor(status) {
      const colorMap = {
        'not_started': 'grey',
        'in_progress': 'warning',
        'completed': 'success'
      };
      return colorMap[status] || 'grey';
    },
    
    formatPaymentStatus(status) {
      const statusMap = {
        'pending': 'Pending',
        'review': 'Under Review',
        'approved': 'Approved',
        'paid': 'Paid',
        'rejected': 'Rejected',
        'not_applicable': 'N/A',
        'no_payout': 'No Payout'
      };
      return statusMap[status] || status;
    },
    
    getPaymentStatusColor(status) {
      const colorMap = {
        'pending': 'orange',
        'review': 'blue',
        'approved': 'light-green',
        'paid': 'success',
        'rejected': 'error',
        'not_applicable': 'grey-lighten-1',
        'no_payout': 'grey'
      };
      return colorMap[status] || 'grey';
    },
    
    goBack() {
      this.$router.push('/experts');
    },
    
    cancel() {
      // Go back to experts list
      this.$router.push('/experts');
    },
    
    saveExpert() {
      this.loading = true;
      this.formErrors = {}; // Clear previous errors
      
      // Validate all required fields - same validation as AccountView
      const errors = {};
      
      // Basic profile validation
      if (!this.expert.firstName || this.expert.firstName.trim() === '') {
        errors.first_name = 'First name is required';
      }
      if (!this.expert.lastName || this.expert.lastName.trim() === '') {
        errors.last_name = 'Last name is required';
      }
      if (!this.expert.email || this.expert.email.trim() === '') {
        errors.email = 'Email is required';
      }
      if (!this.expert.mobilePhone || this.expert.mobilePhone.trim() === '') {
        errors.mobile_phone = 'Mobile phone is required';
      }
      if (!this.expert.gender) {
        errors.gender = 'Gender is required';
      }
      if (!this.expert.expertiseLevel) {
        errors.expertise_level = 'Expertise level is required';
      }
      if (!this.expert.currentCompany || this.expert.currentCompany.trim() === '') {
        errors.current_company = 'Company you work for is required';
      }
      
      // Brand experience validation - REMOVED (No longer required)
      // if (!this.selectedBrands || this.selectedBrands.length === 0) {
      //   errors.brands = 'Please select at least one TCCC brand you have worked with';
      // }
      
      // Expertise areas validation
      if (!this.selectedExpertiseAreas || this.selectedExpertiseAreas.length === 0) {
        errors.expertiseAreas = 'Please select at least one area of expertise';
      }
      
      // Industries validation
      if (!this.selectedIndustries || this.selectedIndustries.length === 0) {
        errors.industries = 'Please select at least one industry you have experience with';
      }
      
      // Geographic expertise validation
      if (!this.selectedGeographicExpertise || this.selectedGeographicExpertise.length === 0) {
        errors.geographicExpertise = 'Please select at least one geographic region where you have expertise';
      }
      
      // Age expertise validation - at least one age group must be rated
      const ageRatings = [
        this.ageExpertise.children,
        this.ageExpertise.teenagers,
        this.ageExpertise.adults,
        this.ageExpertise.seniors
      ];
      if (ageRatings.every(rating => rating === 0)) {
        errors.ageExpertise = 'Please rate your expertise with at least one age group';
      }
      
      // Professional background validation - at least one area must be rated
      const backgroundRatings = [
        this.backgroundData.agency,
        this.backgroundData.academia,
        this.backgroundData.marketing,
        this.backgroundData.marketResearch
      ];
      if (backgroundRatings.every(rating => rating === 0)) {
        errors.backgroundExpertise = 'Please rate your experience in at least one professional background area';
      }
      
      // If there are validation errors, show them and stop
      if (Object.keys(errors).length > 0) {
        this.formErrors = errors;
        this.toast.error('Please fill in all required fields');
        this.loading = false;
        return;
      }
      
      // Prepare data for API - same structure as AccountView
      const updatedData = {
        first_name: this.expert.firstName,
        last_name: this.expert.lastName,
        mobile_phone: this.expert.mobilePhone,
        email: this.expert.email,
        expertise_level: this.expert.expertiseLevel,
        rating: this.expert.rating,
        gender: this.expert.gender,
        works_for_coke: this.expert.worksForCoke, // Include the new field
        linkedin_profile_url: this.expert.linkedinProfileUrl, // Include the new field
        current_company: this.expert.currentCompany,

        // Multi-select fields as arrays of IDs
        industries: this.selectedIndustries,
        expertise_areas: this.selectedExpertiseAreas,
        geographical_expertise: this.selectedGeographicExpertise,
        // brands: this.selectedBrands, // REMOVED - No longer collected
        
        // "Other" text fields
        other_expertise: this.otherExpertise,
        other_industries: this.otherIndustries,
        // other_brand_experience: this.otherBrandExperience,  // REMOVED - No longer collected

        // Age expertise ratings
        age_expertise_children: this.ageExpertise.children,
        age_expertise_teenagers: this.ageExpertise.teenagers,
        age_expertise_adults: this.ageExpertise.adults,
        age_expertise_seniors: this.ageExpertise.seniors,

        // Professional background ratings - removed design fields like AccountView
        agency_experience: this.backgroundData.agency,
        academia_experience: this.backgroundData.academia,
        marketing_experience: this.backgroundData.marketing,
        market_research_experience: this.backgroundData.marketResearch,

        // Additional info
        additional_info: this.additionalInfo
      };
      
      apiService.patch(`experts/${this.expert.id}/`, updatedData)
        .then(() => {
          this.toast.success('Expert profile updated successfully');
          
          // Navigate back to experts list after a short delay
          setTimeout(() => {
            this.$router.push('/experts');
          }, 1500);
        })
        .catch(error => {
          console.error('Error updating expert profile:', error);
          
          // Handle field-specific errors
          if (error && error.response && error.response.data && error.response.data.errors) {
            this.formErrors = error.response.data.errors;
            this.toast.error('Please correct the errors below');
          } else {
            // Handle general errors
            let errorMessage = 'Failed to update expert profile';
            
            if (error && error.response && error.response.data) {
              errorMessage = error.response.data.message || errorMessage;
            }
            
            this.toast.error(errorMessage);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    // Brand name/ID conversion methods - same as AccountView
    convertBrandNameToId(name) {
      if (!name) return null;
      const normalized = String(name).toLowerCase().replace(/[-\s]/g, '');
      const map = {
        'cocacola': 1,
        'sprite': 2,
        'fanta': 3,
        'powerade': 4,
        'minutemaid': 5,
        'dasani': 6,
        'ades': 7,
        'smartwater': 8,
        'topochico': 9,
        'costacoffee': 10,
        'honesttea': 11,
        'simply': 12
      };
      return map[normalized] || null;
    },
    
    // Event handlers for multi-select components - same as AccountView
    updateSelectedBrands(selectedItems) {
      // MultiSelectWithOther emits an array of selected items (objects with id/name)
      // Convert to array of IDs for consistency
      this.selectedBrands = selectedItems.map(item => 
        typeof item === 'object' ? item.id : item
      );
    },
    
    updateOtherBrandExperience(value) {
      this.otherBrandExperience = value;
    },
    
    updateSelectedExpertiseAreas(selectedItems) {
      this.selectedExpertiseAreas = selectedItems.map(item => 
        typeof item === 'object' ? item.id : item
      );
    },
    
    updateOtherExpertise(value) {
      this.otherExpertise = value;
    },
    
    updateSelectedIndustries(selectedItems) {
      this.selectedIndustries = selectedItems.map(item => 
        typeof item === 'object' ? item.id : item
      );
    },
    
    updateOtherIndustries(value) {
      this.otherIndustries = value;
    },
    
    updateSelectedGeographicExpertise(selectedItems) {
      this.selectedGeographicExpertise = selectedItems.map(item => 
        typeof item === 'object' ? item.id : item
      );
    },
    
    updateExpertiseLevel(level) {
      this.expert.expertiseLevel = level;
    },
    
    rateAgeExpertise(data) {
      this.ageExpertise[data.group] = data.level;
    },
    
    rateBackground(data) {
      this.backgroundData[data.area] = data.level;
    },
    
    updateAdditionalInfo(value) {
      this.additionalInfo = value;
    }
  }
};
</script>

<style scoped>
.expert-profile-view {
  padding: 24px;
  background-color: white;
}

.expert-profile-card {
  border-radius: 0;
  overflow: hidden;
  background-color: white;
  box-shadow: none;
}

.expert-avatar {
  border: 2px solid #e0e0e0;
}

.profile-left-column {
  min-width: 150px;
}

.profile-right-column {
  flex: 1;
  max-width: 600px;
}

.age-chip {
  min-width: 100px;
  height: 36px;
  border-radius: 18px;
}

.rounded-lg {
  border-radius: 25px !important;
}

.no-shadow {
  box-shadow: none !important;
}

.expert-qualification {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.qualification-rating {
  margin-top: -5px;
}

/* Compact form styling */
.profile-right-column .v-input {
  margin-bottom: 4px;
}

.profile-right-column .text-subtitle-2 {
  font-weight: 500;
  color: #666;
  line-height: 1.2;
}

/* Dense row styling */
.v-row.dense {
  margin-bottom: 8px;
}

.v-row.dense .v-col {
  padding-top: 4px;
  padding-bottom: 4px;
}

/* Compact age chip */
.age-chip-compact {
  height: 28px;
  border-radius: 14px;
  font-size: 13px;
  min-width: 80px;
}

/* Make inputs more compact */
:deep(.v-input--dense .v-input__control) {
  min-height: 36px;
}

:deep(.v-text-field--outlined.v-input--dense .v-input__control) {
  min-height: 36px;
}

/* Error Message Styles - same as AccountView */
.error-message {
  color: #f44336;
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.error-message::before {
  content: '⚠';
  font-size: 12px;
}

/* General Error Message Section */
.error-message-section {
  margin-bottom: 20px;
}

.error-message-box {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background-color: #ffebee;
  border: 1px solid #f44336;
  border-radius: 4px;
  color: #f44336;
  font-size: 14px;
}

.mb-4 {
  margin-bottom: 20px;
}

.mt-2 {
  margin-top: 8px;
}

/* Participation Table Styles */
.participation-card {
  border-radius: 0;
  overflow: hidden;
  background-color: white;
  box-shadow: none;
  margin-bottom: 24px;
}

.participation-table {
  width: 100%;
}

.participation-table thead th {
  font-weight: 600;
  color: #666;
  background-color: #f5f5f5;
  padding: 12px 16px;
}

.participation-table tbody td {
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.participation-table tbody tr:hover {
  background-color: #f9f9f9;
}

.assessment-title {
  font-weight: 500;
  color: #333;
}

.status-chip {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.completion-info {
  min-width: 120px;
}

.completion-text {
  font-size: 13px;
  font-weight: 500;
  color: #666;
}

.payment-amount {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.py-8 {
  padding-top: 32px;
  padding-bottom: 32px;
}

.text-grey {
  color: #999;
}
</style> 